import React, { useEffect, useState } from "react";
import FilterTextInput from "../../Components/FilterTextInput";
import Button from "../../Components/Button";
import { LogoutCurve, Trash } from "iconsax-react";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
} from "@heroicons/react/24/solid";
import TableContainer from "../../Components/SeatingChart/TableContainer";
import axios from "axios";
import {
  CREATE_TABLE_API,
  DELETE_TABLE_API,
  FETCH_GUEST_DETAILS_API,
  GET_GROUP_MEMBERS_API,
  UPDATE_TABLE_API,
} from "../../Constant/api";
import { useToast } from "../../Components/Toast";
import Modal from "../../Components/Modal";
import TextboxSet from "../../Components/TextboxSet";
import Counter from "../../Components/Counter";
import LoadingInComponent from "../../Components/LoadingInComponent";
import ConfirmationModal from "../../Components/ConfirmationModal";
import { motion as m } from "framer-motion";
import CheckInSliderStep2 from "../../Components/CheckIn/CheckInSliderStep2";
import Can from "../../Components/Can";
import UpdateGuestModal from "../../Components/UpdateGuestModal";

const NormalMode = ({
  tableData,
  handleRefresh,
  handleSaveChanges,
  handleUnassignAllGuests,
  handleUnassignIndividualGuest,
  pendingMoves,
  onAddGuest,
  showFloorplan,
  onToggleFloorplan,
  tableStatuses,
}) => {
  const [collapseAll, setCollapseAll] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    pax: 10,
    is_multi: false,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTableId, setActiveTableId] = useState(null);
  const [modalState, setModalState] = useState({
    type: null,
    table: null,
  });
  const [activeGuestId, setActiveGuestId] = useState(null);
  const [guestToEdit, setGuestToEdit] = useState(null);
  const [guestDetails, setGuestDetails] = useState(null);
  const [groupMembers, setGroupMembers] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const { toast } = useToast();

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleCollapseAll = () => {
    setCollapseAll((prev) => !prev);
  };

  const handleChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };

  const handleViewGuestDetails = () => {
    fetchGuestDetails();
    fetchGroupMembers();
    setIsOpen(true);
  };

  const handleOnChange = (e) => {
    setGuestDetails({ ...guestDetails, [e.target.name]: e.target.value });
  };

  const filteredTableData = tableData
    .filter((table) => table.table_id !== -2)
    .filter((table) => {
      const guestMatch = table.guest_list.some((guest) =>
        guest.guest_name.toLowerCase().includes(searchTerm.toLowerCase()),
      );

      const tableMatch = table.table_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      return tableMatch || guestMatch;
    });

  const handleSubmit = () => {
    setLoading(true);
    axios
      .post(
        CREATE_TABLE_API,
        { ...formData, event_id: sessionStorage.getItem("event_id") },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        },
      )
      .then((response) => {
        toggleModal();
        setLoading(false);
        handleRefresh();
        toast("Table created successfully", "success");
        setFormData({
          name: "",
          description: "",
          pax: 10,
        });
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast("Failed to create table", "error");
      });
  };

  const handleDeleteTable = async (tableId) => {
    setLoading(true);
    if (tableId) {
      handleSaveChanges();
      await axios
        .delete(DELETE_TABLE_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: {
            tableId: tableId,
          },
        })
        .then((res) => {
          handleRefresh();
          setLoading(false);
          toast("Table deleted successfully", "success");
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
          toast("Failed to delete table", "error");
        });
    }
  };

  const handleTableEdit = async (editedData) => {
    const formData = {
      id: editedData.table_id,
      name: editedData.table_name,
      description: editedData.description,
      pax: editedData.pax,
    };
    if (!formData.id || formData.name === "") {
      toast("Missing field is required", "error");
      return;
    }
    setLoading(true);
    axios
      .put(UPDATE_TABLE_API, formData, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          tableId: formData.id,
        },
      })
      .then((res) => {
        setLoading(false);
        setModalState({ type: null, table: null });
        toast("Table updated successfully", "success");
        handleRefresh();
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast(error.response.data.message, "error");
      });
  };

  const fetchGroupMembers = () => {
    setLoading(true);
    axios
      .get(GET_GROUP_MEMBERS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          guestId: activeGuestId,
        },
      })
      .then((response) => {
        setGroupMembers(response.data.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast("Failed to fetch group members", "error");
        setLoading(false);
      });
  };

  const fetchGuestDetails = () => {
    setLoading(true);
    axios
      .get(FETCH_GUEST_DETAILS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          guestId: activeGuestId,
          eventId: sessionStorage.getItem("event_id"),
        },
      })
      .then((res) => {
        setGuestDetails(res.data.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast("Failed to fetch guest details", "error");
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!isOpen) {
      setActiveGuestId(null);
      setGuestDetails(null);
      setGroupMembers(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (activeGuestId) {
      setGuestToEdit(activeGuestId);
    }
  }, [activeGuestId]);

  return (
    <LoadingInComponent isLoading={loading}>
      {/* Global Modals */}
      {modalState.type && modalState.table && (
        <>
          {modalState.type === "delete" ||
          modalState.type === "unseat_all" ||
          modalState.type === "unseat_individual" ? (
            <ConfirmationModal
              title={
                modalState.type === "delete"
                  ? "Delete Table"
                  : modalState.type === "unseat_all"
                    ? "Unseat All Guests"
                    : "Unseat Guest"
              }
              description={
                modalState.type === "delete" ? (
                  <>
                    <p>Are you sure you want to delete this table?</p>
                    <p className="italic text-sm mt-2">
                      (Changes will be saved before proceeding)
                    </p>
                  </>
                ) : modalState.type === "unseat_all" ? (
                  "Are you sure you want to unassign all guest allocated to this table?"
                ) : (
                  "Are you sure you want to unassign this guest from the table?"
                )
              }
              icon={
                modalState.type === "delete" ? (
                  <Trash size="24" color="#FF3030" />
                ) : (
                  <LogoutCurve size="24" color="#FF3030" />
                )
              }
              toggleModal={() => setModalState({ type: null, table: null })}
              onConfirm={() => {
                if (modalState.type === "delete") {
                  handleDeleteTable(modalState.table.table_id);
                } else if (modalState.type === "unseat_all") {
                  handleUnassignAllGuests(modalState.table.table_id);
                } else if (modalState.type === "unseat_individual") {
                  handleUnassignIndividualGuest(modalState.table.guest_id);
                }
                setModalState({ type: null, table: null });
              }}
              confirmLabel={modalState.type === "delete" ? "Delete" : "Confirm"}
            />
          ) : modalState.type === "edit" ? (
            <Modal
              title="Edit Table"
              toggleModal={() => setModalState({ type: null, table: null })}
              handleSubmit={() => handleTableEdit(modalState.table)}
              submitLabel="Save Changes"
            >
              <div className="flex flex-col w-full h-full gap-[24px]">
                <TextboxSet
                  label="Table Name"
                  name="table_name"
                  type="text"
                  value={modalState.table.table_name}
                  required
                  onChange={(e) =>
                    setModalState((prev) => ({
                      ...prev,
                      table: { ...prev.table, table_name: e.target.value },
                    }))
                  }
                />
                <Counter
                  formData={modalState.table}
                  setFormData={(updater) =>
                    setModalState((prev) => {
                      const updatedTable =
                        typeof updater === "function"
                          ? updater(prev.table) // handle functional updates from Counter
                          : { ...prev.table, ...updater }; // handle object updates

                      return { ...prev, table: updatedTable };
                    })
                  }
                />

                <TextboxSet
                  label="Table Description"
                  name="description"
                  type="textarea"
                  value={modalState.table.description}
                  onChange={(e) =>
                    setModalState((prev) => ({
                      ...prev,
                      table: { ...prev.table, description: e.target.value },
                    }))
                  }
                />
              </div>
            </Modal>
          ) : modalState.type === "edit_guest" ? (
            <UpdateGuestModal
              onClose={() => {
                setModalState({ type: null, table: null });
              }}
              onSuccess={handleRefresh}
              guestId={guestToEdit}
            />
          ) : null}
        </>
      )}

      <div className="flex flex-col w-full h-full gap-[24px]">
        {showModal && (
          <Modal
            title="Add Table"
            toggleModal={toggleModal}
            handleSubmit={handleSubmit}
          >
            <div className="flex flex-col w-full h-full gap-[24px]">
              <TextboxSet
                label="Table Name"
                name="name"
                type="text"
                required
                onChange={(e) => handleChange(e)}
              />
              <Counter formData={formData} setFormData={setFormData} />
              <TextboxSet
                label="Table Description"
                name="description"
                type="textarea"
                onChange={(e) => handleChange(e)}
              />
            </div>
          </Modal>
        )}
        {/* Top part buttons and search filter */}
        <div className="flex w-full justify-between items-center">
          <FilterTextInput
            placeholder="Search tables or guests"
            className="w-[300px]"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="flex items-center gap-[12px]">
            <Button
              variant="secondary"
              className="hidden lg:flex"
              onClick={handleCollapseAll}
            >
              {collapseAll ? (
                <ArrowsPointingOutIcon className="size-[8px] lg:size-[16px]" />
              ) : (
                <ArrowsPointingInIcon className="size-[8px] lg:size-[16px]" />
              )}
              {collapseAll ? "Expand" : "Collapse"}
            </Button>
            <Can permission="create_seat">
              <Button variant="secondary" onClick={toggleModal}>
                Add Table
              </Button>
              <Button variant="secondary" onClick={onAddGuest}>
                Add Guest
              </Button>
            </Can>
            <Button className="w-[100px]" onClick={handleSaveChanges}>
              Save
            </Button>
          </div>
        </div>

        {/* Table list with guests */}
        <div className="h-full">
          {tableData && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-[12px] lg:gap-[24px]">
              {filteredTableData.map((table, index) => (
                <TableContainer
                  data={table}
                  key={table.table_id}
                  index={index}
                  collapseAll={collapseAll}
                  activeTableId={activeTableId}
                  setActiveTableId={setActiveTableId}
                  openModal={(type, table) => setModalState({ type, table })}
                  handleDeleteTable={handleDeleteTable}
                  handleUnassignAllGuests={handleUnassignAllGuests}
                  handleEditTable={handleTableEdit}
                  activeGuestId={activeGuestId}
                  setActiveGuestId={setActiveGuestId}
                  pendingMoves={pendingMoves}
                  handleViewGuestDetails={handleViewGuestDetails}
                  status={tableStatuses?.[table.table_id]}
                />
              ))}
            </div>
          )}
        </div>

        {guestDetails && isOpen && groupMembers && (
          <m.div
            key="step2"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed right-0 top-0 w-6/12 lg:w-[40%] h-full bg-white shadow-lg"
          >
            <CheckInSliderStep2
              guestInfo={guestDetails}
              handleOnChange={handleOnChange}
              groupMembers={groupMembers}
              loading={loading}
              setIsOpen={setIsOpen}
            />
          </m.div>
        )}
      </div>
    </LoadingInComponent>
  );
};

export default NormalMode;
